/* PTL → PoD Analytics.
   For PTL shipments delivered in the chosen dates: how many have a PoD, how it came in (API / Manual), whether it came
   on time, and how long the missing ones have been waiting — overall, per courier and per client, down to the AWB.
   Layout, top to bottom: filters (shared) → two tabs. Overview: two speedometers + key numbers → what needs attention +
   pending by age → trend → courier / client scorecard (search, filters, paging). PoD Shipments: the AWB list; every
   number on Overview opens it already filtered. The tab is kept in the URL (?view=shipments). Courier reminder emails
   open in a side drawer. Every list can be downloaded as CSV. Plan: POD_ANALYTICS.md. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import _ from 'lodash'
import { Alert, Autocomplete, Box, Button, Card, CardContent, Chip, Drawer, Grid, IconButton, Tab, Tabs, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import SearchIcon from '@mui/icons-material/Search'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import CloseIcon from '@mui/icons-material/Close'
import { useSnackbar } from 'notistack'
import PageTitle from '../../components/PageTitle'
import { FilterCard, FilterCardContent, FilterRow, FilterDateField, FieldLabel } from '../../common/FilterBar'
import { apiConstants, get, getDownload } from '../../common/constants'
import { COURIER_LIST_QUERY_PAYLOAD } from '../../common/ProshipFormComponents'
import { useGetAllCourierPartnersCachedQuery } from '../../redux/commonApiCalls'
import MerchantPicker from '../ptl-billing/components/MerchantPicker'
import { FilterSlot } from '../ptl-billing/components/FilterSelect'
import PodAgingStrip from './PodAgingStrip'
import PodBreakdownTable from './PodBreakdownTable'
import PodShipmentsTable from './PodShipmentsTable'
import PodSettingsDialog from './PodSettingsDialog'
import PodInsights from './PodInsights'
import PodTrendCard from './PodTrendCard'
import PodHeadline from './PodHeadline'
import PodRemindersCard from './PodRemindersCard'
import { POD_BASE, QUICK_RANGES, SOURCE_LABEL, TIMELINESS, TONE, downloadFile, errText, num, toParams, ymd } from './podUtils'

const MAX_DAYS = 92
const DEFAULT_RANGE = QUICK_RANGES[1] // last 30 days

const cardSx = { border: 1, borderColor: 'divider', mb: 2 }

export default function PodAnalytics() {
  const { enqueueSnackbar } = useSnackbar()
  const [params, setParams] = useSearchParams()

  // ── page filters (kept in the URL so a view can be shared)
  const [defFrom, defTo] = DEFAULT_RANGE.range()
  const from = params.get('from') || ymd(defFrom)
  const to = params.get('to') || ymd(defTo)
  const merchantOid = params.get('client') || ''
  const courierId = params.get('courier') || ''
  const view = params.get('view') === 'shipments' ? 'shipments' : 'overview'
  // Filter edits replace the history entry; switching tab (push) adds one, so Back returns to the previous tab.
  const setPageFilter = useCallback((patch, { push = false } = {}) => {
    const next = Object.fromEntries(params.entries())
    Object.entries(patch).forEach(([k, v]) => { if (v === null || v === '' || v === undefined) delete next[k]; else next[k] = v })
    setParams(next, { replace: !push })
  }, [params, setParams])

  const rangeDays = Math.round((new Date(to) - new Date(from)) / 86400000) + 1
  const rangeError = !from || !to ? 'Pick both dates.' : from > to ? 'The start date is after the end date.' : rangeDays > MAX_DAYS ? `Pick at most ${MAX_DAYS} days at a time.` : null
  const filtersChanged = from !== ymd(defFrom) || to !== ymd(defTo) || !!merchantOid || !!courierId
  const activeQuick = QUICK_RANGES.find((q) => { const [a, b] = q.range(); return ymd(a) === from && ymd(b) === to })?.key

  // ── couriers for the picker (PTL only)
  const { data: allCouriers = [] } = useGetAllCourierPartnersCachedQuery(COURIER_LIST_QUERY_PAYLOAD)
  const courierOptions = useMemo(() => _.orderBy(allCouriers.filter((c) => c.supported_shipment === 'B2B'), 'name')
    .map((c) => ({ value: c.id, label: c.parent && c.parent !== c.name ? `${c.name} (${c.parent})` : c.name })), [allCouriers])

  // ── summary
  const [summary, setSummary] = useState(null)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [summaryError, setSummaryError] = useState(null)
  const [reload, setReload] = useState(0)
  const base = useMemo(() => toParams({ from, to, merchantOid, courierId }), [from, to, merchantOid, courierId])

  useEffect(() => {
    if (rangeError) return
    let alive = true
    setSummaryLoading(true)
    setSummaryError(null)
    get(`${POD_BASE}/summary`, base)
      .then(({ data }) => { if (alive) setSummary(data) })
      .catch((e) => { if (alive) setSummaryError(errText(e, 'Could not load PoD analytics.')) })
      .finally(() => { if (alive) setSummaryLoading(false) })
    return () => { alive = false }
  }, [base, rangeError, reload])

  // ── shipment table filters
  const [podStatus, setPodStatus] = useState('ALL')
  const [agingBucket, setAgingBucket] = useState(null)
  const [timeliness, setTimeliness] = useState('')
  const [podSource, setPodSource] = useState('')
  const [awbInput, setAwbInput] = useState('')
  const [awb, setAwb] = useState('')
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(25)
  const tabsRef = useRef(null)
  const topRef = useRef(null)
  const remindersRef = useRef(null)
  const [remindersOpen, setRemindersOpen] = useState(false)
  const [mailStatus, setMailStatus] = useState(null) // { autoEnabled, couriers } from the reminders drawer

  useEffect(() => { const t = setTimeout(() => setAwb(awbInput.trim()), 400); return () => clearTimeout(t) }, [awbInput])
  useEffect(() => { setPage(0) }, [base, podStatus, agingBucket, timeliness, podSource, awb, size])

  const rowParams = useMemo(() => toParams({ ...base, podStatus: podStatus === 'ALL' ? '' : podStatus, agingBucket, timeliness, podSource, awb }),
    [base, podStatus, agingBucket, timeliness, podSource, awb])

  const [shipments, setShipments] = useState(null)
  const [shipmentsLoading, setShipmentsLoading] = useState(false)
  useEffect(() => {
    if (rangeError || view !== 'shipments') return // loaded only when the tab is open
    let alive = true
    setShipmentsLoading(true)
    get(`${POD_BASE}/shipments`, { ...rowParams, page, size })
      .then(({ data }) => { if (alive) setShipments(data) })
      .catch((e) => { if (alive) enqueueSnackbar(errText(e, 'Could not load the shipments.'), { variant: 'error' }) })
      .finally(() => { if (alive) setShipmentsLoading(false) })
    return () => { alive = false }
  }, [rowParams, page, size, rangeError, reload, view, enqueueSnackbar])

  const setView = (v) => setPageFilter({ view: v === 'shipments' ? 'shipments' : null }, { push: true })
  /**
   * Sets the table filters in one go, opens the PoD Shipments tab and brings the tabs into view. `pagePatch` narrows
   * the page in the same URL update (two separate updates would lose the first).
   */
  const showRows = (next, pagePatch = {}) => {
    setPodStatus(next.podStatus ?? 'ALL')
    setAgingBucket(next.agingBucket ?? null)
    setTimeliness(next.timeliness ?? '')
    setPodSource(next.podSource ?? '')
    setAwbInput('')
    setPageFilter({ ...pagePatch, view: 'shipments' }, { push: view !== 'shipments' })
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    setTimeout(() => tabsRef.current?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' }), 50)
  }
  const clearRowFilters = () => { setAgingBucket(null); setTimeliness(''); setPodSource(''); setAwbInput('') }

  // Actions behind the "Needs attention" buttons: narrow the page (courier / client), the table, or both.
  const insightActions = useMemo(() => ({
    showRows,
    focus: (patch) => setPageFilter(patch),
    focusRows: (patch, rows) => showRows(rows, patch),
  }), [setPageFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  const [downloading, setDownloading] = useState(false)
  const download = async (extra, label) => {
    setDownloading(true)
    try {
      const q = new URLSearchParams(toParams({ ...rowParams, ...extra })).toString()
      await downloadFile(getDownload, `${apiConstants.BASE_LOCAL}${POD_BASE}/shipments.csv?${q}`, `PTL_PoD_${label || 'Shipments'}_${from}_to_${to}.csv`)
    } catch (e) {
      enqueueSnackbar('Could not download the list.', { variant: 'error' })
    } finally {
      setDownloading(false)
    }
  }

  const [tab, setTab] = useState('courier')
  const [settingsOpen, setSettingsOpen] = useState(false)

  const t = summary?.totals
  const labels = summary?.bucketLabels || []
  const merchantName = summary?.byClient?.find((c) => c.key === merchantOid)?.name
  const courierName = courierOptions.find((c) => c.value === courierId)?.label

  const rowFilterChips = [
    agingBucket != null && { key: 'b', label: `Pending ${labels[agingBucket]?.toLowerCase() || ''}`, clear: () => setAgingBucket(null) },
    timeliness && { key: 't', label: TIMELINESS[timeliness]?.label, clear: () => setTimeliness('') },
    podSource && { key: 's', label: `Via ${SOURCE_LABEL[podSource]}`, clear: () => setPodSource('') },
  ].filter(Boolean)

  return (
    <Box ref={topRef} sx={{ scrollMarginTop: 88 }}>
      <PageTitle>PoD Analytics</PageTitle>

      {/* ── header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
          PTL shipments delivered in the dates you pick · on time = PoD within {summary?.onTimeHours ?? 24} h
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={mailStatus ? `Daily 10:00 email is ${mailStatus.autoEnabled ? 'on' : 'off'}. ${mailStatus.couriers
            ? `${mailStatus.couriers} ${mailStatus.couriers === 1 ? 'courier has' : 'couriers have'} PoDs pending past the on-time window.` : 'No courier has PoDs waiting.'}` : ''}>
            <Button variant='outlined' onClick={() => setRemindersOpen(true)} startIcon={<MailOutlineIcon />} sx={{ textTransform: 'none', gap: 0.25 }}>
              Courier emails
              {mailStatus?.couriers > 0 && (
                <Box component='span' sx={{ ml: 1, px: 0.75, py: 0.125, borderRadius: 5, fontSize: 12, fontWeight: 700, lineHeight: 1.5,
                  bgcolor: TONE.orange.bg, color: TONE.orange.fg, border: 1, borderColor: TONE.orange.border }}>{mailStatus.couriers} waiting</Box>
              )}
              {mailStatus && <Box component='span' sx={{ ml: 1, fontSize: 12, fontWeight: 500, color: 'text.secondary' }}>Daily {mailStatus.autoEnabled ? 'on' : 'off'}</Box>}
            </Button>
          </Tooltip>
          <Button variant='outlined' startIcon={<SettingsOutlinedIcon />} onClick={() => setSettingsOpen(true)} sx={{ textTransform: 'none' }}>
            Settings
          </Button>
        </Box>
      </Box>

      {/* ── filters */}
      <FilterCard variant='outlined'>
        <FilterCardContent>
          <FilterRow>
            <FilterSlot label='Period' sx={{ flex: '0 1 auto', maxWidth: '100%', overflowX: 'auto' }}>
              <ToggleButtonGroup size='small' exclusive value={activeQuick || null} aria-label='Quick period'
                onChange={(_e, k) => { const q = QUICK_RANGES.find((x) => x.key === k); if (q) { const [a, b] = q.range(); setPageFilter({ from: ymd(a), to: ymd(b) }) } }}>
                {QUICK_RANGES.map((q) => <ToggleButton key={q.key} value={q.key} sx={{ textTransform: 'none', px: 1, height: 33, whiteSpace: 'nowrap', fontSize: 13 }}>{q.label}</ToggleButton>)}
              </ToggleButtonGroup>
            </FilterSlot>
            <FilterDateField sx={{ flex: '0 0 140px', minWidth: 140 }}>
              <FieldLabel>Delivered from</FieldLabel>
              <TextField type='date' size='small' value={from} inputProps={{ max: to, 'aria-label': 'Delivered from' }}
                onChange={(e) => setPageFilter({ from: e.target.value })} />
            </FilterDateField>
            <FilterDateField sx={{ flex: '0 0 140px', minWidth: 140 }}>
              <FieldLabel>Delivered to</FieldLabel>
              <TextField type='date' size='small' value={to} inputProps={{ min: from, max: ymd(new Date()), 'aria-label': 'Delivered to' }}
                onChange={(e) => setPageFilter({ to: e.target.value })} />
            </FilterDateField>
            <FilterSlot label='Client' sx={{ flex: '1 1 160px', minWidth: 160, maxWidth: 280 }}>
              <MerchantPicker value={merchantOid || null} onChange={(v) => setPageFilter({ client: v })} label='' placeholder='Search client' sx={{ minWidth: 0 }} />
            </FilterSlot>
            <FilterSlot label='Courier' sx={{ flex: '1 1 150px', minWidth: 150, maxWidth: 260 }}>
              <Autocomplete size='small' options={courierOptions} value={courierOptions.find((o) => o.value === courierId) || null}
                onChange={(_e, o) => setPageFilter({ courier: o ? o.value : null })} isOptionEqualToValue={(a, b) => a.value === b.value}
                renderInput={(p) => <TextField {...p} placeholder='Search courier' />} />
            </FilterSlot>
            {/* only when something differs from the default view (last 30 days, all clients, all couriers) */}
            {filtersChanged && <Button size='small' onClick={() => setParams(view === 'shipments' ? { view } : {}, { replace: true })} sx={{ textTransform: 'none', height: 33 }}>Reset</Button>}
          </FilterRow>
        </FilterCardContent>
      </FilterCard>

      {/* ── focus: the page is narrowed to one client / courier */}
      {(merchantOid || courierId) && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2, px: 1.5, py: 1, borderRadius: 2, bgcolor: 'action.selected' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Showing only</Typography>
          {merchantOid && <Chip size='small' label={`Client: ${merchantName || 'selected client'}`} onDelete={() => setPageFilter({ client: null })}
            deleteIcon={<CloseIcon aria-label='Show all clients' />} sx={{ bgcolor: 'background.paper', fontWeight: 600, '& .MuiChip-label': { textTransform: 'none' } }} />}
          {courierId && <Chip size='small' label={`Courier: ${courierName || 'selected courier'}`} onDelete={() => setPageFilter({ courier: null })}
            deleteIcon={<CloseIcon aria-label='Show all couriers' />} sx={{ bgcolor: 'background.paper', fontWeight: 600, '& .MuiChip-label': { textTransform: 'none' } }} />}
          <Button size='small' onClick={() => setPageFilter({ client: null, courier: null })} sx={{ textTransform: 'none', ml: 'auto' }}>Show everything</Button>
        </Box>
      )}

      {rangeError && <Alert severity='warning' sx={{ mb: 2 }}>{rangeError}</Alert>}
      {summaryError && <Alert severity='error' sx={{ mb: 2 }} action={<Button color='inherit' size='small' onClick={() => setReload((n) => n + 1)}>Try again</Button>}>{summaryError}</Alert>}

      {/* ── page tabs: the overview, or the AWB list */}
      <Box ref={tabsRef} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, scrollMarginTop: 88 }}>
        <Tabs value={view} onChange={(_e, v) => setView(v)} aria-label='PoD Analytics views'>
          <Tab value='overview' label='Overview' sx={{ textTransform: 'none', fontWeight: 700, fontSize: 15, minHeight: 44 }} />
          <Tab value='shipments' label={`PoD Shipments${t ? ` (${num(t.delivered)})` : ''}`}
            sx={{ textTransform: 'none', fontWeight: 700, fontSize: 15, minHeight: 44 }} />
        </Tabs>
      </Box>

      {view === 'overview' && (<>
      {/* ── speedometers + key numbers */}
      <PodHeadline summary={summary} loading={summaryLoading} rows={{ podStatus, agingBucket, timeliness, podSource }} onShow={showRows} />

      {/* ── what to act on */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} lg={7}><PodInsights summary={summary} loading={summaryLoading} actions={insightActions} /></Grid>
        <Grid item xs={12} lg={5}>
          <PodAgingStrip labels={labels} counts={t?.pendingByBucket || []} selected={podStatus === 'PENDING' ? agingBucket : null} downloading={downloading}
            onSelect={(i) => (i == null ? setAgingBucket(null) : showRows({ podStatus: 'PENDING', agingBucket: i }))}
            onDownload={(i) => download({ podStatus: 'PENDING', agingBucket: i, timeliness: '', podSource: '' }, `Pending_${labels[i]?.replace(/\W+/g, '_')}`)}
            onDownloadAll={() => download({ podStatus: 'PENDING', agingBucket: '', timeliness: '', podSource: '' }, 'Pending')} />
        </Grid>
      </Grid>

      <Box sx={{ mb: 2 }}><PodTrendCard summary={summary} /></Box>

      {/* ── courier / client scorecard */}
      <Card elevation={0} sx={cardSx}>
        <CardContent sx={{ pt: 1, pb: '8px !important' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: '-1px' }}>
              <Tab value='courier' label={`By courier${summary ? ` (${summary.byCourier.length})` : ''}`} sx={{ textTransform: 'none', fontWeight: 600 }} />
              <Tab value='client' label={`By client${summary ? ` (${summary.byClient.length})` : ''}`} sx={{ textTransform: 'none', fontWeight: 600 }} />
            </Tabs>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Most pending first. Click a row to see the whole page for that {tab}.</Typography>
          </Box>
          <PodBreakdownTable kind={tab} rows={tab === 'courier' ? summary?.byCourier : summary?.byClient} bucketLabels={labels}
            onTimeHours={summary?.onTimeHours} receivedTargetPct={summary?.receivedTargetPct} onTimeTargetPct={summary?.onTimeTargetPct}
            focusedKey={tab === 'courier' ? courierId : merchantOid} fileLabel={`${from}_to_${to}`}
            onPick={(r) => {
              if (!r.key) return
              setPageFilter(tab === 'courier' ? { courier: r.key } : { client: r.key })
              topRef.current?.scrollIntoView({ behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' })
            }}
            onEmail={tab === 'courier' ? (r) => { setRemindersOpen(true); remindersRef.current?.emailCourier(r.key) } : undefined} />
        </CardContent>
      </Card>
      </>)}

      {/* ── PoD Shipments tab: the AWB list (client / courier / dates come from the filter bar above) */}
      {view === 'shipments' && (
      <Card elevation={0} sx={cardSx}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <ToggleButtonGroup size='small' exclusive value={podStatus} aria-label='PoD status'
                onChange={(_e, v) => { if (v) { setPodStatus(v); if (v !== 'PENDING') setAgingBucket(null); if (v === 'PENDING') { setTimeliness(''); setPodSource('') } } }}>
                <ToggleButton value='ALL' sx={{ textTransform: 'none', px: 1.5 }}>All {t ? `(${num(t.delivered)})` : ''}</ToggleButton>
                <ToggleButton value='PENDING' sx={{ textTransform: 'none', px: 1.5 }}>PoD pending {t ? `(${num(t.pending)})` : ''}</ToggleButton>
                <ToggleButton value='RECEIVED' sx={{ textTransform: 'none', px: 1.5 }}>PoD received {t ? `(${num(t.received)})` : ''}</ToggleButton>
              </ToggleButtonGroup>
              {rowFilterChips.map((c) => <Chip key={c.key} size='small' color='primary' label={c.label} onDelete={c.clear} sx={{ '& .MuiChip-label': { textTransform: 'none' } }} />)}
              {rowFilterChips.length > 1 && <Button size='small' onClick={clearRowFilters} sx={{ textTransform: 'none' }}>Clear</Button>}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* same page-level client filter as the filter bar, so both tabs stay in step */}
              <MerchantPicker value={merchantOid || null} onChange={(v) => setPageFilter({ client: v })} label='' placeholder='Search client'
                sx={{ width: 240, minWidth: 0 }} />
              <TextField size='small' placeholder='Search AWB' value={awbInput} onChange={(e) => setAwbInput(e.target.value)}
                inputProps={{ 'aria-label': 'Search AWB' }}
                InputProps={{ startAdornment: <SearchIcon fontSize='small' sx={{ color: 'text.secondary', mr: 0.5 }} /> }} sx={{ width: 200 }} />
              <LoadingButton size='small' variant='contained' startIcon={<FileDownloadOutlinedIcon />} loading={downloading} disabled={!shipments?.total}
                onClick={() => download({}, 'Shipments')} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                Download {shipments?.total ? `(${num(shipments.total)})` : ''}
              </LoadingButton>
            </Box>
          </Box>
          <PodShipmentsTable data={shipments} loading={shipmentsLoading} bucketLabels={labels} onTimeHours={summary?.onTimeHours}
            page={page} size={size} onPage={setPage} onSize={setSize} />
        </CardContent>
      </Card>
      )}

      {/* ── courier reminder emails (kept mounted so the scorecard's mail icon works while it is closed) */}
      <Drawer anchor='right' open={remindersOpen} onClose={() => setRemindersOpen(false)} keepMounted
        PaperProps={{ sx: { width: { xs: '100%', md: 760 }, maxWidth: '100%' } }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pt: 1 }}>
          <IconButton aria-label='Close courier emails' onClick={() => setRemindersOpen(false)}><CloseIcon /></IconButton>
        </Box>
        <PodRemindersCard ref={remindersRef} embedded onStatus={setMailStatus} />
      </Drawer>

      <PodSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} onSaved={() => setReload((n) => n + 1)} />
    </Box>
  )
}
